import { loadConfig } from "@hobbyshopify/vite-cfg";
import * as Sentry from "@sentry/react";
import { type ReactNode, useEffect } from "react";
import { createRoutesFromChildren, matchRoutes, Routes, useLocation, useNavigationType } from "react-router-dom";

const DEFAULT_PRODUCTION_TRACES_SAMPLE_RATE = 0.2;
const DEFAULT_NON_PRODUCTION_TRACES_SAMPLE_RATE = 1.0;

function numberConfig(name: string, fallback: number): number {
  const value = loadConfig(name);
  if (value === undefined || value === "") {
    return fallback;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function environment(): string {
  return loadConfig("VITE_SENTRY_ENVIRONMENT") ?? import.meta.env.MODE ?? "development";
}

function defaultTracesSampleRate(): number {
  return environment() === "production"
    ? DEFAULT_PRODUCTION_TRACES_SAMPLE_RATE
    : DEFAULT_NON_PRODUCTION_TRACES_SAMPLE_RATE;
}

export const isSentryEnabled = Boolean(loadConfig("VITE_SENTRY_DSN"));

// Capture the one-time bearer in memory, then scrub it before Sentry and the router start.
let initialClaimToken: string | null = null;
if (typeof window !== "undefined") {
  const match = window.location.pathname.match(/^\/claim\/([^/]+)\/?$/);
  if (match) {
    try {
      initialClaimToken = decodeURIComponent(match[1]);
      window.history.replaceState(window.history.state, "", "/claim");
    } catch {
      initialClaimToken = null;
      window.history.replaceState(window.history.state, "", "/claim");
    }
  }
}

export function getInitialClaimToken(): string | null {
  return initialClaimToken;
}

export function clearInitialClaimToken(): void {
  initialClaimToken = null;
}

function isClaimLink(value?: string): boolean {
  if (!value) return false;
  try {
    return /^\/claim\/[^/]+\/?$/.test(new URL(value, window.location.origin).pathname);
  } catch {
    return false;
  }
}

if (isSentryEnabled) {
  Sentry.init({
    dsn: loadConfig("VITE_SENTRY_DSN"),
    environment: environment(),
    release: loadConfig("VITE_SENTRY_RELEASE"),
    sendDefaultPii: false,
    tracesSampleRate: numberConfig("VITE_SENTRY_TRACES_SAMPLE_RATE", defaultTracesSampleRate()),
    tracePropagationTargets: [/^\/api\//, /^\/api$/, window.location.origin],
    integrations: [
      Sentry.reactRouterBrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
    beforeSend(event) {
      if (isClaimLink(event.request?.url) || isClaimLink(event.transaction)) return null;

      if (event.request?.headers) {
        delete event.request.headers.Authorization;
        delete event.request.headers.authorization;
      }

      return event;
    },
    beforeSendTransaction(event) {
      if (event.transaction === "/health" || isClaimLink(event.request?.url) || isClaimLink(event.transaction)) {
        return null;
      }

      return event;
    },
  });
}

export const SentryRoutes = Sentry.wrapReactRouterRouting(Routes);

export function SentryErrorBoundary({ children }: { children: ReactNode }) {
  if (!isSentryEnabled) {
    return children;
  }

  return <Sentry.ErrorBoundary fallback={<div>Something went wrong.</div>}>{children}</Sentry.ErrorBoundary>;
}

export function setSentryUser(userId: string | null): void {
  if (!isSentryEnabled) {
    return;
  }

  Sentry.setUser(userId ? { id: userId } : null);
}

export function setSentryStore(storeId: string | null): void {
  if (!isSentryEnabled) {
    return;
  }

  if (storeId) {
    Sentry.setTag("store.id", storeId);
    Sentry.setContext("store", { id: storeId });
  } else {
    Sentry.setTag("store.id", undefined);
    Sentry.setContext("store", null);
  }
}
